import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { useTranslation } from 'react-i18next';

const fmt = (n) => new Intl.NumberFormat('ru-RU').format(n ?? 0);

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
}

export default function AdminSmsBillingPage() {
    const { t } = useTranslation(['common', 'nav', 'messages', 'errors', 'admin']);
    const role = localStorage.getItem('role') || '';
    const [overview, setOverview] = useState(null);
    const [depositSchoolId, setDepositSchoolId] = useState('');
    const [depositAmount, setDepositAmount] = useState('');
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (role === 'Admin') load();
    }, [role]);

    async function load() {
        try {
            const res = await adminApi().get('/admin/sms-billing/overview');
            setOverview(res.data);
        } catch {
            setMsg(t('admin:smsBilling.loadError'));
        }
    }

    async function doDeposit(e) {
        e.preventDefault();
        const schoolId = Number(depositSchoolId);
        const amount = Number(depositAmount);
        if (!schoolId || amount <= 0) return;
        try {
            await adminApi().post(`/admin/schools/${schoolId}/sms-billing/deposit`, {
                amount,
                note: t('admin:smsBilling.adminDepositNote')
            });
            setMsg(t('admin:smsBilling.depositSuccess', { schoolId, amount }));
            setDepositAmount('');
            load();
        } catch (err) {
            setMsg(err.response?.data?.message || t('common:error'));
        }
    }

    if (role !== 'Admin') {
        return (
            <OwnerPageLayout title={t('admin:smsBilling.title')} maxWidth={1100}>
                <p style={{ textAlign: 'center' }}>{t('admin:smsBilling.adminOnly')}</p>
            </OwnerPageLayout>
        );
    }

    const s = overview?.summary;
    const tariff = overview?.tariff;

    return (
        <OwnerPageLayout
            title={t('admin:smsBilling.title')}
            maxWidth={1100}
            actions={
                <a href="/admin/integrations" className="sp-btn sp-btn--ghost">
                    {t('admin:smsBilling.settingsLink')}
                </a>
            }
        >

                {tariff && (
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: 12, marginBottom: 24
                    }}>
                        <div style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>{t('admin:smsBilling.providerCost')}</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(tariff.providerCostPerMessage)} ₽</div>
                        </div>
                        <div style={{ background: '#fff', padding: 16, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: '#64748b' }}>{t('admin:smsBilling.schoolPrice')}</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(tariff.schoolPricePerMessage)} ₽</div>
                        </div>
                        <div style={{ background: '#e8f5e9', padding: 16, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: '#2e7d32' }}>{t('admin:smsBilling.totalRevenue')}</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(s?.totalRevenue)} ₽</div>
                        </div>
                        <div style={{ background: '#fff3e0', padding: 16, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: '#e65100' }}>{t('admin:smsBilling.providerSpend')}</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(s?.totalProvider)} ₽</div>
                        </div>
                        <div style={{ background: '#ede7f6', padding: 16, borderRadius: 12 }}>
                            <div style={{ fontSize: 12, color: '#EB3333' }}>{t('admin:smsBilling.margin')}</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>{fmt(s?.totalMargin)} ₽ ({s?.marginPercent}%)</div>
                        </div>
                    </div>
                )}

                <form onSubmit={doDeposit} style={{
                    background: '#fff', padding: 20, borderRadius: 12, marginBottom: 24,
                    display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end'
                }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600 }}>{t('admin:smsBilling.schoolId')}</label>
                        <input value={depositSchoolId} onChange={e => setDepositSchoolId(e.target.value)}
                            style={{ padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', width: 100 }} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600 }}>{t('admin:smsBilling.amount')}</label>
                        <input type="number" value={depositAmount} onChange={e => setDepositAmount(e.target.value)}
                            style={{ padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', width: 120 }} />
                    </div>
                    <button type="submit" style={{
                        padding: '10px 20px', background: '#EB3333', color: '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer'
                    }}>
                        {t('admin:smsBilling.depositButton')}
                    </button>
                    {msg && <span style={{ fontSize: 13 }}>{msg}</span>}
                </form>

                <table style={{ width: '100%', background: '#fff', borderRadius: 12, borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colSchool')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colBalance')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colSmsOn')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colEmailOnly')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colSent')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colCharged')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colProviderCost')}</th>
                            <th style={{ padding: 12 }}>{t('admin:smsBilling.colMargin')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(overview?.schools || []).map(row => (
                            <tr key={row.schoolId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: 12 }}>{row.schoolName} <span style={{ color: '#94a3b8' }}>#{row.schoolId}</span></td>
                                <td style={{ padding: 12, fontWeight: 600 }}>{fmt(row.balance)} ₽</td>
                                <td style={{ padding: 12 }}>{row.isServiceEnabled ? '✓' : '—'}</td>
                                <td style={{ padding: 12 }}>{row.preferEmailOnly ? '✓' : '—'}</td>
                                <td style={{ padding: 12 }}>{row.totalMessagesSent}</td>
                                <td style={{ padding: 12 }}>{fmt(row.totalCharged)} ₽</td>
                                <td style={{ padding: 12 }}>{fmt(row.providerCost)} ₽</td>
                                <td style={{ padding: 12 }}>{fmt(row.margin)} ₽</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
        </OwnerPageLayout>
    );
}
